import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BackLink } from '../../components/ui/ui'
import useReveal from '../../hooks/useReveal'
import cv from '../../data/cv'
import './CV.scss'

const PDF_PATH = '/resume.pdf'
const PDF_NAME = 'Paradzai-Gwira-AI-Software-Engineer-CV.pdf'

const sections = [
  { id: 'cv-summary', label: 'Profile' },
  { id: 'cv-skills', label: 'Skills' },
  { id: 'cv-experience', label: 'Experience' },
  { id: 'cv-lab', label: 'R&D Lab' },
  { id: 'cv-education', label: 'Education' }
]

const Section = ({ id, title, children }) => (
  <section id={id} className="cv-section" data-reveal>
    <h2 className="cv-section__title"><span>{title}</span></h2>
    {children}
  </section>
)

const CV = () => {
  const rootRef = useRef(null)
  const navigate = useNavigate()
  const [active, setActive] = useState(sections[0].id)
  const [copied, setCopied] = useState(false)
  useReveal(rootRef)

  // Highlight the rail link for the section currently in view.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => { if (entry.isIntersecting) setActive(entry.target.id) }),
      { rootMargin: '-35% 0px -55% 0px' }
    )
    sections.forEach(({ id }) => { const el = document.getElementById(id); if (el) observer.observe(el) })
    return () => observer.disconnect()
  }, [])

  const jump = (id) => {
    const el = document.getElementById(id)
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 150, behavior: 'smooth' })
  }

  const copyLink = () => {
    navigator.clipboard?.writeText(window.location.href)
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }

  const coreStack = ['LLMs & RAG', 'AI agents', 'Java / Spring Boot', 'Python', 'Node.js', 'Go', 'ClickHouse', 'Kubernetes', 'SIP / VoIP']

  return (
    <main className="ui-page cv-page" ref={rootRef}>
      <div className="cv-toolbar">
        <div className="ui-container cv-toolbar__inner">
          <BackLink onClick={() => navigate('/')}>Portfolio</BackLink>
          <div className="cv-toolbar__title">
            <strong>Curriculum Vitae</strong>
            <span>A4 · 3 pages · ATS-friendly</span>
          </div>
          <div className="cv-toolbar__actions">
            <button type="button" className="cv-tool" onClick={copyLink} aria-label="Copy link to this CV">
              <i className={`fas ${copied ? 'fa-check' : 'fa-link'}`} /><span>{copied ? 'Copied' : 'Copy link'}</span>
            </button>
            <button type="button" className="cv-tool" onClick={() => window.print()} aria-label="Print CV">
              <i className="fas fa-print" /><span>Print</span>
            </button>
            <a className="cv-tool cv-tool--primary" href={PDF_PATH} download={PDF_NAME} data-magnetic>
              <i className="fas fa-file-arrow-down" /><span>Download PDF</span>
            </a>
          </div>
        </div>
      </div>

      <div className="ui-container cv-layout">
        <aside className="cv-rail">
          <div className="cv-rail__card">
            <p className="cv-rail__label">Recruiter snapshot</p>
            <dl className="cv-facts">
              <div><dt>Role</dt><dd>AI Software Engineer</dd></div>
              <div><dt>Experience</dt><dd>5+ years, production</dd></div>
              <div><dt>Focus</dt><dd>GenAI · Distributed systems · Telecom & data</dd></div>
              <div><dt>Work style</dt><dd>Formally trained autodidact · R&D-driven</dd></div>
              <div><dt>Availability</dt><dd><span className="cv-dot" /> {cv.availability}</dd></div>
            </dl>
            <div className="cv-rail__chips">{coreStack.map((item) => <span key={item}>{item}</span>)}</div>
          </div>

          <nav className="cv-rail__nav" aria-label="CV sections">
            {sections.map((section, index) => (
              <button key={section.id} type="button" className={active === section.id ? 'is-active' : ''} onClick={() => jump(section.id)}>
                <span>0{index + 1}</span>{section.label}
              </button>
            ))}
          </nav>
        </aside>

        <article className="cv-paper" aria-label={`Curriculum vitae of ${cv.name}`}>
          <header className="cv-head">
            <div>
              <h1>{cv.name}</h1>
              <p className="cv-head__headline">{cv.headline}</p>
              <p className="cv-head__tagline">{cv.tagline}</p>
            </div>
            <ul className="cv-contact">
              {cv.contact.map((item) => (
                <li key={item.label}>
                  <i className={item.icon.startsWith('fa-brands') ? item.icon : `fas ${item.icon}`} aria-hidden="true" />
                  <a href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer">{item.label}</a>
                </li>
              ))}
              <li><i className="fas fa-plane-departure" aria-hidden="true" /><span>{cv.availability}</span></li>
            </ul>
          </header>

          <Section id="cv-summary" title="Profile">
            <p className="cv-summary">{cv.summary}</p>
            <div className="cv-highlights">
              {cv.highlights.map((item) => (
                <div key={item.label}><strong>{item.value}</strong><span>{item.label}</span></div>
              ))}
            </div>
            <p className="cv-apps"><strong>Apps in public stores:</strong> {cv.apps.join(' · ')} <span>({cv.appPlatforms})</span></p>
          </Section>

          <Section id="cv-skills" title="Core Skills">
            <div className="cv-skills">
              {cv.skills.map((group) => (
                <div className="cv-skills__row" key={group.group}>
                  <h3>{group.group}</h3>
                  <p>{group.items.join(' · ')}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section id="cv-experience" title="Experience">
            <div className="cv-jobs">
              {cv.experience.map((job) => (
                <div className="cv-job" key={job.company}>
                  <div className="cv-job__head">
                    <div>
                      <h3>{job.role}</h3>
                      <p className="cv-job__company">{job.company}</p>
                    </div>
                    <div className="cv-job__meta">
                      <time>{job.period}</time>
                      <span>{job.mode}</span>
                    </div>
                  </div>
                  <ul className="cv-bullets">
                    {job.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </Section>

          <Section id="cv-lab" title="R&D Lab & Open Source">
            <div className="cv-lab">
              {cv.lab.map((item) => (
                <div className="cv-lab__item" key={item.title}>
                  <h3>{item.href ? <a href={item.href} target="_blank" rel="noreferrer">{item.title}</a> : item.title}</h3>
                  <p className="cv-lab__stack">{item.stack}</p>
                  <p>{item.text}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section id="cv-education" title="Education & Certification">
            <div className="cv-edu">
              {cv.education.map((item) => (
                <div className="cv-edu__row" key={item.title}>
                  <div><h3>{item.title}</h3><p>{item.place}</p></div>
                  <time>{item.period}</time>
                </div>
              ))}
              {cv.certifications.map((item) => (
                <div className="cv-edu__row" key={item.title}>
                  <div><h3>{item.title}</h3></div>
                  <span className="cv-badge">{item.status}</span>
                </div>
              ))}
            </div>
            <div className="cv-extra">
              <p><strong>Languages:</strong> {cv.languages.join(' · ')}</p>
              <p><strong>Interests:</strong> {cv.interests}</p>
            </div>
          </Section>
        </article>
      </div>
    </main>
  )
}

export default CV
